import { DisplayConfig, JsxgraphBehaviorConfig, LabelConfig, WidgetConfig } from '@examdojo/core/widget';
import type { ThemeColorName } from '@examdojo/styles';

// ------------------------------------------------------------------
// 1. PARAMS CONFIGURATION
// ------------------------------------------------------------------

export interface ArithmeticSeriesParams {
  /** First term (a or u₁) */
  a?: number;
  /** Common difference */
  d?: number;
  /** Number of terms to display */
  numTerms?: number;
}

// ------------------------------------------------------------------
// 2. DISPLAY CONFIGURATION
// ------------------------------------------------------------------

export interface ArithmeticSeriesFeatureConfig {
  enabled?: boolean;
  label?: LabelConfig;
  color?: ThemeColorName;
}

export interface ArithmeticSeriesDisplayConfig extends DisplayConfig {
  /** Show the cumulative sum line (Sₙ step function) */
  sumLine?: boolean | ArithmeticSeriesFeatureConfig;
  /** Show bars for individual terms */
  bars?: boolean | ArithmeticSeriesFeatureConfig;
  /** Show points on the sum line */
  points?: boolean | ArithmeticSeriesFeatureConfig;
  /** Show term labels (u₁, u₂, etc.) on x-axis */
  termLabels?: boolean | ArithmeticSeriesFeatureConfig;
  /** Show sum tooltip on hover */
  sumTooltip?: boolean;
}

// ------------------------------------------------------------------
// 3. BEHAVIOR CONFIGURATION
// ------------------------------------------------------------------

export type ArithmeticSeriesActionTarget = 'canvas' | 'bars' | 'sumLine' | 'points';

export type ArithmeticSeriesActionType = 'UPDATE_PARAMS' | 'FIT_TO_SERIES' | 'HIGHLIGHT_SUM';

export type ArithmeticSeriesBehaviorConfig = JsxgraphBehaviorConfig<
  ArithmeticSeriesActionTarget,
  ArithmeticSeriesActionType
>;

// ------------------------------------------------------------------
// 4. MASTER WIDGET CONFIG
// ------------------------------------------------------------------

export type ArithmeticSeriesWidgetConfig = WidgetConfig<
  'arithmetic-series',
  ArithmeticSeriesParams,
  ArithmeticSeriesDisplayConfig,
  ArithmeticSeriesBehaviorConfig
>;

// ------------------------------------------------------------------
// 5. DEFAULTS
// ------------------------------------------------------------------

export const DEFAULT_ARITHMETIC_SERIES_PARAMS: Required<ArithmeticSeriesParams> = {
  a: 2,
  d: 3,
  numTerms: 7,
};

export const DEFAULT_ARITHMETIC_SERIES_FEATURE_COLORS: Record<
  'sumLine' | 'bars' | 'points' | 'highlight',
  ThemeColorName
> = {
  sumLine: 'neutral',
  bars: 'sky',
  points: 'neutral',
  highlight: 'indigo',
};
